import * as React from "react";
import { useMemo } from "react";
import { useHistory } from "react-router";
import {
  UserCheck,
  Rss,
  Image,
  Send,
  BookOpen,
  Video,
  PlayCircle,
  Contact,
  TrendingUp,
  TrendingDown,
} from "lucide-react";

import { WebSettings } from "@/interfaces";
import { CoachService } from "@/services";
import { useSocialCoachSession, URLS, useAccountSettings } from "@/utils";
import { Skeleton } from "@/components/ui";

import "./styles.scss";
import { HealthCard, HealthInfo, HealthStat } from "../HealthCard";
import { useQuery } from "@tanstack/react-query";

interface OwnProps {}
type Props = OwnProps;

const HeathStatsFC: React.FC<Props> = () => {
  const history = useHistory();
  const { coachId } = useSocialCoachSession();
  const { hasAccountSetting } = useAccountSettings(coachId);

  const { data: stats, isLoading } = useQuery({
    queryKey: ["coachStatistics", coachId],
    queryFn: () => CoachService.statistics(coachId!),
    meta: { persist: true },
    enabled: !!coachId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const diffValues = (now: number | undefined, prev: number | undefined, label: string): HealthStat[] => {
    const values: HealthStat[] = [
      {
        value: now === undefined ? 0 : now,
        label: label,
      },
    ];
    if (now !== undefined && prev !== undefined) {
      const diff = now - prev;
      const color = diff === 0 ? undefined : now > prev ? "text-green-600" : "text-red-600";
      const label =
        diff === 0
          ? ""
          : `${diff >= 0 ? "+" : "-"}${((Math.abs(diff) / (prev || 1)) * 100).toFixed(0)}% vs. previous period`;
      values.push({
        prefix: diff === 0 ? undefined : diff > 0 ? TrendingUp : TrendingDown,
        value: diff === 0 ? undefined : Math.abs(diff),
        label,
        color: color,
      });
    }
    return values;
  };

  const healthList: HealthInfo[] = useMemo(() => {
    if (!stats) return [];

    const report = stats.featureEngagementReport;
    const list: HealthInfo[] = [];

    list.push({
      key: "player-health",
      title: "Players",
      primaryValue: {
        value: report.activePlayers,
        label: `Total Active`,
      },
      secondaryValues: [{ value: report.scPlusPlayers, label: `With SocialCoach+`, color: "text-sky-500" }],
      icon: UserCheck,
      onClick: () => {
        history.push(URLS.coach.players, { selectedTab: "ACTIVE" });
      },
    });

    list.push({
      key: "campaign-health",
      title: "Campaigns",
      primaryValue: {
        value: report.remainingPrompts,
        label: `Remaining Prompts`,
      },
      secondaryValues: [
        {
          value: report.enrolledPlayers,
          label: report.enrolledPlayers === 1 ? `Player Actively Enrolled` : `Players Actively Enrolled`,
        },
      ],
      icon: Rss,
      onClick: () => {
        history.push(URLS.coach.campaigns);
      },
    });

    list.push({
      key: "prompt-health",
      title: "Prompts",
      primaryValue: {
        value: report.totalPrompts,
        label: `Total`,
      },
      secondaryValues: [...diffValues(report.prompts90, report.promptsPrev90, "Created Last 90d")],
      icon: Image,
      onClick: () => {
        history.push(URLS.coach.prompts);
      },
    });

    list.push({
      key: "post-health",
      title: "Posts",
      primaryValue: {
        value: report.totalPosts,
        label: `Total Posted`,
      },
      secondaryValues: [...diffValues(report.posts90, report.postsPrev90, "Posted Last 90d")],
      icon: Send,
      onClick: () => {
        history.push(URLS.coach.calendar);
      },
    });

    if (
      hasAccountSetting(WebSettings.ACCESS_SCRIPT_LIBRARY) &&
      !hasAccountSetting(WebSettings.EXCLUDE_GLOBAL_SCRIPTS)
    ) {
      list.push({
        key: "script-health",
        title: "Video Scripts",
        primaryValue: {
          value: report.totalScripts,
          label: `Total`,
        },
        secondaryValues: [...diffValues(report.scripts90, report.scriptsPrev90, "Created Last 90d")],
        icon: BookOpen,
        onClick: () => {
          history.push(URLS.coach.videoScripts);
        },
      });
    }

    if (hasAccountSetting(WebSettings.SC_PLUS_TIER)) {
      list.push({
        key: "project-health",
        title: "Video Projects",
        primaryValue: {
          value: report.totalProjects,
          label: `Total`,
        },
        secondaryValues: [...diffValues(report.projects90, report.projectsPrev90, "Created Last 90d")],
        icon: Video,
        onClick: () => {
          history.push(URLS.coach.videoProjects);
        },
      });
    }

    list.push({
      key: "mvp-health",
      title: "Video Pages",
      primaryValue: {
        value: report.totalPages,
        label: `Total`,
      },
      secondaryValues: [...diffValues(report.pages90, report.pagesPrev90, "Created Last 90d")],
      icon: PlayCircle,
      onClick: () => {
        history.push(URLS.coach.mvpSettings);
      },
    });

    if (hasAccountSetting(WebSettings.ALLOW_ACCESS_TO_PLAYER_CARDS)) {
      list.push({
        key: "player-card-health",
        title: "Player Cards",
        primaryValue: {
          value: report.totalCards,
          label: `Total Cards`,
        },
        secondaryValues: [...diffValues(report.cards90, report.cardsPrev90, "Created Last 90d")],
        icon: Contact,
        onClick: () => {
          history.push(URLS.coach.playerCards);
        },
      });
    }

    return list;
  }, [stats, history, hasAccountSetting]);

  return (
    <div className="coachDashboardStatistics">
      <div className="statsGrid">
        {isLoading
          ? Array.from({ length: 6 }).map((_, idx) => (
              <div
                key={`skeleton-${idx}`}
                style={{ width: "250px" }}
                className="bg-white p-3.5 rounded-xl border border-slate-200/50 h-[115px] flex flex-col justify-between animate-pulse select-none"
              >
                <div className="flex items-center gap-1.5">
                  <Skeleton className="size-4 rounded-md" />
                  <Skeleton className="h-4 w-20" />
                </div>
                <div className="flex items-baseline justify-between mt-2.5">
                  <Skeleton className="h-8 w-16" />
                  <Skeleton className="h-4 w-12" />
                </div>
                <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-12" />
                </div>
              </div>
            ))
          : healthList.map(info => <HealthCard key={`health-card-${info.key}`} info={info} width={250} />)}
      </div>
    </div>
  );
};

export const HeathStats = HeathStatsFC;
